import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadialBarChart, RadialBar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  ChartBarIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowPathIcon,
  CpuChipIcon,
  QueueListIcon,
  BoltIcon,
  ServerIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

import apiClient from '../services/api';

const MetricsDashboard = ({ userId }) => {
    const [systemStats, setSystemStats] = useState(null);
    const [userStats, setUserStats] = useState(null);
    const [queueDepth, setQueueDepth] = useState(null);
    const [performance, setPerformance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [refreshInterval, setRefreshInterval] = useState(5000);

    // Fetch all metrics data
    const fetchMetrics = async () => {
        try {
            const [sysResponse, queueResponse, perfResponse, userResponse] = await Promise.allSettled([
                apiClient.get('/metrics/stats'),
                apiClient.get('/metrics/queue'),
                apiClient.get('/metrics/performance'),
                userId ? apiClient.get(`/metrics/users/${userId}`) : Promise.resolve(null)
            ]);

            if (sysResponse.status === 'fulfilled' && sysResponse.value?.data) {
                setSystemStats(sysResponse.value.data);
            }

            if (queueResponse.status === 'fulfilled' && queueResponse.value?.data) {
                setQueueDepth(queueResponse.value.data);
            }

            if (perfResponse.status === 'fulfilled' && perfResponse.value?.data) {
                setPerformance(perfResponse.value.data);
            }

            if (userResponse && userResponse.status === 'fulfilled' && userResponse.value?.data) {
                setUserStats(userResponse.value.data);
            }

            setLoading(false);
            setError(null);
        } catch (err) {
            console.error('Error fetching metrics:', err);
            setError('Failed to fetch metrics data');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics();

        if (autoRefresh) {
            const interval = setInterval(fetchMetrics, refreshInterval);
            return () => clearInterval(interval);
        }
    }, [userId, autoRefresh, refreshInterval]); // eslint-disable-line react-hooks/exhaustive-deps

    // Format functions
    const formatDuration = (seconds) => {
        if (seconds < 60) return `${seconds.toFixed(1)}s`;
        if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
        return `${(seconds / 3600).toFixed(1)}h`;
    };

    const formatPercent = (value) => {
        return `${value.toFixed(1)}%`;
    };

    // Chart colors
    const COLORS = {
        primary: '#4F46E5',
        secondary: '#7C3AED',
        success: '#10B981',
        warning: '#F59E0B',
        danger: '#EF4444',
        info: '#3B82F6',
        neutral: '#6B7280'
    };

    // Prepare data for charts
    const getJobStatusData = () => {
        if (!systemStats) return [];
        return [
            { name: 'Processing', value: systemStats.queue.processing, color: COLORS.primary },
            { name: 'Queued', value: systemStats.queue.queued, color: COLORS.warning },
            { name: 'Completed', value: systemStats.queue.completed, color: COLORS.success },
            { name: 'Failed', value: systemStats.queue.failed, color: COLORS.danger }
        ].filter(item => item.value > 0);
    };

    const getPerformanceData = () => {
        if (!performance) return [];
        return [
            { name: '1h ago', completed: 0, failed: 0 },
            { name: '45m ago', completed: Math.floor(performance.job_performance.completed_last_hour * 0.25), failed: Math.floor(performance.job_performance.failed_last_hour * 0.25) },
            { name: '30m ago', completed: Math.floor(performance.job_performance.completed_last_hour * 0.5), failed: Math.floor(performance.job_performance.failed_last_hour * 0.5) },
            { name: '15m ago', completed: Math.floor(performance.job_performance.completed_last_hour * 0.75), failed: Math.floor(performance.job_performance.failed_last_hour * 0.75) },
            { name: 'Now', completed: performance.job_performance.completed_last_hour, failed: performance.job_performance.failed_last_hour }
        ];
    };

    const getRadialData = () => {
        if (!performance) return [];
        return [{
            name: 'Completion',
            value: performance.job_performance.completion_rate_percent,
            fill: performance.job_performance.completion_rate_percent > 90 ? COLORS.success : COLORS.warning
        }];
    };

    const getQueueVisualizationData = () => {
        if (!queueDepth) return [];
        return [
            { subject: 'Queue Depth', A: queueDepth.queue_depth, fullMark: 100 },
            { subject: 'Processing', A: queueDepth.processing, fullMark: 100 },
            { subject: 'Wait Time', A: Math.min(queueDepth.estimated_wait_seconds, 100), fullMark: 100 },
            { subject: 'Requirements', A: Math.min(queueDepth.total_requirements_queued / 10, 100), fullMark: 100 }
        ];
    };

    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center min-h-screen"
            >
                <div className="text-center">
                    <div className="relative">
                        <div className="w-20 h-20 border-4 border-primary-200 dark:border-primary-800 rounded-full animate-pulse"></div>
                        <div className="w-20 h-20 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin absolute top-0 left-0"></div>
                    </div>
                    <p className="mt-4 text-neutral-600 dark:text-neutral-400 font-medium">Loading metrics...</p>
                </div>
            </motion.div>
        );
    }

    if (error) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-8 border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/10 max-w-md mx-auto mt-8"
            >
                <div className="text-center">
                    <ExclamationTriangleIcon className="w-12 h-12 text-danger-600 dark:text-danger-400 mx-auto mb-4" />
                    <p className="text-danger-700 dark:text-danger-400 font-medium mb-4">{error}</p>
                    <button
                        onClick={fetchMetrics}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-danger-600 hover:bg-danger-700 text-white rounded-lg transition-colors"
                    >
                        <ArrowPathIcon className="w-5 h-5" />
                        Retry
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6 pb-8"
        >
            {/* Header with controls */}
            <div className="sticky top-0 z-30 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-lg -mx-8 px-8 py-4 border-b border-neutral-200 dark:border-neutral-800">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl">
                            <ChartBarIcon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Performance Analytics</h2>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">Real-time system metrics and insights</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className={`relative inline-flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                            autoRefresh ? 'bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800' :
                            'bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700'
                        }`}>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-primary-600"></div>
                                <span className={`ml-3 text-sm font-medium ${
                                    autoRefresh ? 'text-success-700 dark:text-success-400' : 'text-neutral-700 dark:text-neutral-300'
                                }`}>
                                    {autoRefresh ? (
                                        <span className="flex items-center gap-1">
                                            <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                                            Live
                                        </span>
                                    ) : 'Paused'}
                                </span>
                            </label>
                            {autoRefresh && (
                                <AnimatePresence>
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        className="flex items-center gap-2"
                                    >
                                        <div className="w-px h-4 bg-success-300 dark:bg-success-700"></div>
                                        <select
                                            value={refreshInterval}
                                            onChange={(e) => setRefreshInterval(Number(e.target.value))}
                                            className="bg-transparent border-0 text-sm font-medium text-success-700 dark:text-success-400 focus:outline-none cursor-pointer"
                                        >
                                            <option value={2000}>Every 2s</option>
                                            <option value={5000}>Every 5s</option>
                                            <option value={10000}>Every 10s</option>
                                            <option value={30000}>Every 30s</option>
                                        </select>
                                    </motion.div>
                                </AnimatePresence>
                            )}
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={fetchMetrics}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg transition-all shadow-lg shadow-primary-500/25"
                        >
                            <ArrowPathIcon className="w-4 h-4" />
                            Refresh
                        </motion.button>
                    </div>
                </div>
            </div>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {systemStats && (
                    <>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="card p-6 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-900/10 border-primary-200 dark:border-primary-800"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">Active Jobs</span>
                                <CogIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin-slow" />
                            </div>
                            <div className="text-3xl font-bold text-primary-900 dark:text-primary-100">
                                {systemStats.queue.processing}
                            </div>
                            <div className="flex items-center gap-1 mt-2">
                                {systemStats.queue.processing > 0 ? (
                                    <ArrowTrendingUpIcon className="w-4 h-4 text-primary-600" />
                                ) : (
                                    <ArrowTrendingDownIcon className="w-4 h-4 text-primary-400" />
                                )}
                                <span className="text-xs text-primary-600 dark:text-primary-400">
                                    {systemStats.queue.processing > 0 ? 'System busy' : 'System idle'}
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="card p-6 bg-gradient-to-br from-warning-50 to-warning-100 dark:from-warning-900/20 dark:to-warning-900/10 border-warning-200 dark:border-warning-800"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-warning-700 dark:text-warning-300">In Queue</span>
                                <QueueListIcon className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                            </div>
                            <div className="text-3xl font-bold text-warning-900 dark:text-warning-100">
                                {systemStats.queue.queued}
                            </div>
                            <div className="flex items-center gap-1 mt-2">
                                <ClockIcon className="w-4 h-4 text-warning-600" />
                                <span className="text-xs text-warning-600 dark:text-warning-400">
                                    Est. {queueDepth ? formatDuration(queueDepth.estimated_wait_seconds) : '0s'} wait
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="card p-6 bg-gradient-to-br from-success-50 to-success-100 dark:from-success-900/20 dark:to-success-900/10 border-success-200 dark:border-success-800"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-success-700 dark:text-success-300">Completed</span>
                                <CheckCircleIconSolid className="w-5 h-5 text-success-600 dark:text-success-400" />
                            </div>
                            <div className="text-3xl font-bold text-success-900 dark:text-success-100">
                                {systemStats.queue.completed}
                            </div>
                            <div className="flex items-center gap-1 mt-2">
                                <SparklesIcon className="w-4 h-4 text-success-600" />
                                <span className="text-xs text-success-600 dark:text-success-400">
                                    {performance ? `${formatPercent(performance.job_performance.completion_rate_percent)} success rate` : '0% success rate'}
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="card p-6 bg-gradient-to-br from-danger-50 to-danger-100 dark:from-danger-900/20 dark:to-danger-900/10 border-danger-200 dark:border-danger-800"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-danger-700 dark:text-danger-300">Failed</span>
                                <XCircleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                            </div>
                            <div className="text-3xl font-bold text-danger-900 dark:text-danger-100">
                                {systemStats.queue.failed}
                            </div>
                            <div className="flex items-center gap-1 mt-2">
                                <ExclamationTriangleIcon className="w-4 h-4 text-danger-600" />
                                <span className="text-xs text-danger-600 dark:text-danger-400">
                                    {performance ? `${performance.job_performance.failed_last_hour} in last hour` : '0 in last hour'}
                                </span>
                            </div>
                        </motion.div>
                    </>
                )}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Job Status Distribution */}
                {systemStats && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 }}
                        className="card p-6"
                    >
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Job Distribution</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={getJobStatusData()}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {getJobStatusData().map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-2 gap-2 mt-4">
                            {getJobStatusData().map((item, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                                    <span className="text-sm text-neutral-600 dark:text-neutral-400">{item.name}: {item.value}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Performance Trend */}
                {performance && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 }}
                        className="card p-6"
                    >
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Performance Trend</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={getPerformanceData()}>
                                <defs>
                                    <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor={COLORS.success} stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.danger} stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor={COLORS.danger} stopOpacity={0.1}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                                <YAxis stroke="#6b7280" fontSize={12} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                />
                                <Area type="monotone" dataKey="completed" stroke={COLORS.success} fillOpacity={1} fill="url(#colorCompleted)" />
                                <Area type="monotone" dataKey="failed" stroke={COLORS.danger} fillOpacity={1} fill="url(#colorFailed)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </motion.div>
                )}

                {/* Completion Rate Gauge */}
                {performance && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.6 }}
                        className="card p-6"
                    >
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Success Rate</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <RadialBarChart cx="50%" cy="50%" innerRadius="30%" outerRadius="90%" data={getRadialData()}>
                                <RadialBar dataKey="value" cornerRadius={10} fill={getRadialData()[0]?.fill || COLORS.primary} />
                                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="text-3xl font-bold fill-current text-neutral-900 dark:text-white">
                                    {performance.job_performance.completion_rate_percent.toFixed(0)}%
                                </text>
                            </RadialBarChart>
                        </ResponsiveContainer>
                        <div className="text-center mt-2">
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                {performance.job_performance.completion_rate_percent > 90 ? 'Excellent performance' :
                                 performance.job_performance.completion_rate_percent > 70 ? 'Good performance' :
                                 'Needs improvement'}
                            </p>
                        </div>
                    </motion.div>
                )}

                {/* Queue Analysis Radar */}
                {queueDepth && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7 }}
                        className="card p-6"
                    >
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Queue Analysis</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <RadarChart data={getQueueVisualizationData()}>
                                <PolarGrid stroke="#e5e7eb" />
                                <PolarAngleAxis dataKey="subject" stroke="#6b7280" fontSize={12} />
                                <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="#6b7280" />
                                <Radar name="Metrics" dataKey="A" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.6} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </motion.div>
                )}
            </div>

            {/* Detailed Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* LLM Performance */}
                {performance && performance.llm_api && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="card p-6"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-gradient-to-br from-secondary-100 to-secondary-200 dark:from-secondary-900/30 dark:to-secondary-900/20 rounded-xl">
                                <CpuChipIcon className="w-6 h-6 text-secondary-600 dark:text-secondary-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">LLM Performance</h3>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400">Model processing metrics</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm text-neutral-600 dark:text-neutral-400">API Utilization</span>
                                    <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                                        {Math.min(100, ((performance.concurrency.users_with_active_jobs / Math.max(1, performance.concurrency.max_concurrent_jobs)) * 100)).toFixed(0)}%
                                    </span>
                                </div>
                                <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-secondary-500 to-secondary-600 transition-all duration-300"
                                        style={{ width: `${Math.min(100, (performance.concurrency.users_with_active_jobs / Math.max(1, performance.concurrency.max_concurrent_jobs)) * 100)}%` }}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">{performance.llm_api.total_requests || 0}</p>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Total Requests</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                        {performance.llm_api.average_wait_time !== undefined && performance.llm_api.average_wait_time !== null
                                            ? `${performance.llm_api.average_wait_time.toFixed(2)}s`
                                            : '0.00s'}
                                    </p>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Avg Response</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* User Activity */}
                {userStats && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 }}
                        className="card p-6"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-900/20 rounded-xl">
                                <UserGroupIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Your Activity</h3>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400">Personal metrics</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="text-center p-2 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                    <p className="text-lg font-bold text-primary-600 dark:text-primary-400">{userStats.processing}</p>
                                    <p className="text-xs text-neutral-600 dark:text-neutral-400">Active</p>
                                </div>
                                <div className="text-center p-2 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                    <p className="text-lg font-bold text-warning-600 dark:text-warning-400">{userStats.queued}</p>
                                    <p className="text-xs text-neutral-600 dark:text-neutral-400">Queued</p>
                                </div>
                                <div className="text-center p-2 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                    <p className="text-lg font-bold text-success-600 dark:text-success-400">{userStats.completed}</p>
                                    <p className="text-xs text-neutral-600 dark:text-neutral-400">Done</p>
                                </div>
                            </div>
                            {userStats.quota && (
                                <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-neutral-600 dark:text-neutral-400">Hourly Limit</span>
                                        <span className="font-medium text-neutral-900 dark:text-white">
                                            {userStats.quota.submissions_last_hour}/10
                                        </span>
                                    </div>
                                    <div className="w-full h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden mt-1">
                                        <div
                                            className={`h-full transition-all duration-300 ${
                                                userStats.quota.submissions_last_hour > 8 ? 'bg-danger-500' :
                                                userStats.quota.submissions_last_hour > 5 ? 'bg-warning-500' : 'bg-success-500'
                                            }`}
                                            style={{ width: `${(userStats.quota.submissions_last_hour / 10) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* System Health */}
                {systemStats && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.0 }}
                        className="card p-6"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-gradient-to-br from-success-100 to-success-200 dark:from-success-900/30 dark:to-success-900/20 rounded-xl">
                                <ServerIcon className="w-6 h-6 text-success-600 dark:text-success-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">System Health</h3>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400">Overall status</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                                    <span className="text-sm text-neutral-700 dark:text-neutral-300">API Status</span>
                                </div>
                                <span className="text-sm font-medium text-success-600 dark:text-success-400">Healthy</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <UserGroupIcon className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                                    <span className="text-sm text-neutral-700 dark:text-neutral-300">Active Users</span>
                                </div>
                                <span className="text-sm font-medium text-neutral-900 dark:text-white">{systemStats.active_users}</span>
                            </div>
                            {systemStats.rate_limiting && (
                                <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <BoltIcon className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                                        <span className="text-sm text-neutral-700 dark:text-neutral-300">Rate Limit</span>
                                    </div>
                                    <span className="text-sm font-medium text-neutral-900 dark:text-white">
                                        {systemStats.rate_limiting.available_request_tokens}/{systemStats.rate_limiting.request_capacity || 10}
                                    </span>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Recent Jobs Table */}
            {userStats && userStats.recent_jobs && userStats.recent_jobs.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1 }}
                    className="card p-6"
                >
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Recent Jobs</h3>
                        <span className="text-sm text-neutral-500 dark:text-neutral-400">Last 10 jobs</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left border-b border-neutral-200 dark:border-neutral-700">
                                    <th className="pb-3 text-sm font-medium text-neutral-600 dark:text-neutral-400">Job ID</th>
                                    <th className="pb-3 text-sm font-medium text-neutral-600 dark:text-neutral-400">Status</th>
                                    <th className="pb-3 text-sm font-medium text-neutral-600 dark:text-neutral-400">Progress</th>
                                    <th className="pb-3 text-sm font-medium text-neutral-600 dark:text-neutral-400">Duration</th>
                                    <th className="pb-3 text-sm font-medium text-neutral-600 dark:text-neutral-400">Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {userStats.recent_jobs.map((job, index) => (
                                    <motion.tr
                                        key={job.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                                    >
                                        <td className="py-3">
                                            <code className="text-xs font-mono text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
                                                {job.id.substring(0, 8)}
                                            </code>
                                        </td>
                                        <td className="py-3">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                                job.status === 'success' ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400' :
                                                job.status === 'processing' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400' :
                                                job.status === 'queued' ? 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400' :
                                                job.status === 'failed' ? 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400' :
                                                'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400'
                                            }`}>
                                                {job.status === 'success' && <CheckCircleIcon className="w-3 h-3" />}
                                                {job.status === 'processing' && <CogIcon className="w-3 h-3 animate-spin" />}
                                                {job.status === 'queued' && <ClockIcon className="w-3 h-3" />}
                                                {job.status === 'failed' && <XCircleIcon className="w-3 h-3" />}
                                                {job.status}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 max-w-[100px]">
                                                    <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full transition-all duration-300 ${
                                                                job.status === 'success' ? 'bg-success-500' :
                                                                job.status === 'processing' ? 'bg-primary-500' :
                                                                job.status === 'failed' ? 'bg-danger-500' :
                                                                'bg-neutral-400'
                                                            }`}
                                                            style={{ width: `${(job.requirements_processed / job.total_requirements) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <span className="text-xs text-neutral-600 dark:text-neutral-400">
                                                    {job.requirements_processed}/{job.total_requirements}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <span className="text-sm text-neutral-700 dark:text-neutral-300">
                                                {job.completed_at && job.created_at ?
                                                    formatDuration((new Date(job.completed_at) - new Date(job.created_at)) / 1000) :
                                                    '-'
                                                }
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                                {new Date(job.created_at).toLocaleTimeString()}
                                            </span>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default MetricsDashboard;